import { useState, useCallback, useRef, useEffect } from "react";

export const useSocialAuth = ({
  clientId: configClientId,
  onAuthSuccess,
  setConsumerToken,
  showSnackbar,
  onRequiresDob, // New callback for DOB collection flow
  onStateChange, // For navigating to collectDob state
}) => {
  const [socialLoading, setSocialLoading] = useState("");
  const [error, setError] = useState("");
  
  const cleanupRef = useRef(null);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const getClientId = useCallback(() => {
    const clientId = configClientId?.toString() || localStorage.getItem("consumerClientId");
    if (clientId) {
      localStorage.setItem("consumerClientId", clientId);
    }
    return clientId;
  }, [configClientId]);

  const handleGoogleLogin = useCallback((options = {}) => {
    const { mode = "login", dateOfBirth = null } = options;
    setSocialLoading("google");
    setError("");

    const clientId = getClientId();
    console.log("🔍 Client ID in Google login:", clientId, "Mode:", mode);

    if (!clientId) {
      const errorMsg = "Client ID not found. Please refresh the page and try again.";
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
      setSocialLoading("");
      return;
    }

    // Build popup URL with mode and dateOfBirth
    let popupUrl = `${window.location.origin}/google-login-popup?clientId=${encodeURIComponent(clientId)}&mode=${encodeURIComponent(mode)}`;
    if (dateOfBirth) {
      popupUrl += `&dateOfBirth=${encodeURIComponent(dateOfBirth)}`;
    }

    const popup = window.open(
      popupUrl,
      "GoogleLoginPopup",
      "width=500,height=600"
    );

    let timeoutId;
    let checkClosedId;

    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      // Handle requiresDob response - need to collect DOB before completing registration
      if (event.data && event.data.requiresDob) {
        console.log("📋 Google OAuth requires DOB collection:", event.data);
        const pendingData = {
          provider: "google",
          email: event.data.email,
          name: event.data.name,
          oauthSessionToken: event.data.oauthSessionToken, // Use session token instead of code
          clientId: event.data.clientId || configClientId,
        };
        
        // Store pending OAuth data and navigate to collectDob
        if (onRequiresDob) {
          onRequiresDob(pendingData);
        }
        if (onStateChange) {
          onStateChange("collectDob");
        }
        
        setSocialLoading("");
        cleanup();
        if (popup) popup.close();
        return;
      }

      if (event.data && event.data.googleToken) {
        try {
          console.log("✅ Google login success data:", event.data);
          if (setConsumerToken) {
            setConsumerToken(event.data.googleToken);
          } else {
            localStorage.setItem("consumerToken", event.data.googleToken);
          }
          onAuthSuccess({
            isAuthenticated: true,
            consumerId: event.data.consumerId,
            email: event.data.email,
            clientId: event.data.clientId || configClientId,
            requiresTwoFactor: false,
            credits: event.data.credits,
          });
        } catch (err) {
          console.error("❌ Error processing Google login success:", err);
          const errorMsg = `Google login processing failed: ${err instanceof Error ? err.message : "Unknown error"}`;
          setError(errorMsg);
          showSnackbar?.(errorMsg, "error");
        } finally {
          setSocialLoading("");
        }
        cleanup();
        if (popup) popup.close();
      }

      if (event.data && event.data.googleError) {
        console.error("❌ Google login error:", event.data);
        const errorMsg = event.data.googleErrorDescription || event.data.googleError || "Google login failed";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        setSocialLoading("");
        cleanup();
        if (popup) popup.close();
      }
    };

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeoutId);
      clearInterval(checkClosedId);
      cleanupRef.current = null;
    };

    cleanupRef.current = cleanup;
    window.addEventListener("message", handleMessage);

    checkClosedId = setInterval(() => {
      if (popup?.closed) {
        console.log("🔄 Google login popup was closed");
        setSocialLoading("");
        cleanup();
      }
    }, 1000);

    timeoutId = setTimeout(() => {
      console.log("⏰ Google login timeout");
      const errorMsg = "Google login timeout. Please try again.";
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
      setSocialLoading("");
      cleanup();
      if (popup && !popup.closed) popup.close();
    }, 120000);
  }, [getClientId, configClientId, onAuthSuccess, setConsumerToken, showSnackbar, onRequiresDob, onStateChange]);

  const handleAppleLogin = useCallback((options = {}) => {
    const { mode = "login", dateOfBirth = null } = options;
    setSocialLoading("apple");
    setError("");

    const clientId = getClientId();
    console.log("🔍 Client ID in Apple login:", clientId, "Mode:", mode);

    if (!clientId) {
      const errorMsg = "Client ID not found. Please refresh the page and try again.";
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
      setSocialLoading("");
      return;
    }

    // Build popup URL with mode and dateOfBirth
    let popupUrl = `${window.location.origin}/apple-login-popup?clientId=${encodeURIComponent(clientId)}&mode=${encodeURIComponent(mode)}`;
    if (dateOfBirth) {
      popupUrl += `&dateOfBirth=${encodeURIComponent(dateOfBirth)}`;
    }

    const popup = window.open(
      popupUrl,
      "AppleLoginPopup",
      "width=500,height=600"
    );

    let timeoutId;
    let checkClosedId;

    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      // Handle requiresDob response - need to collect DOB before completing registration
      if (event.data && event.data.requiresDob && event.data.provider === "apple") {
        console.log("📋 Apple OAuth requires DOB collection:", event.data);
        const pendingData = {
          provider: "apple",
          email: event.data.email,
          name: event.data.name,
          oauthSessionToken: event.data.oauthSessionToken, // Use session token instead of identity token
          clientId: event.data.clientId || configClientId,
        };
        
        // Store pending OAuth data and navigate to collectDob
        if (onRequiresDob) {
          onRequiresDob(pendingData);
        }
        if (onStateChange) {
          onStateChange("collectDob");
        }
        
        setSocialLoading("");
        cleanup();
        if (popup) popup.close();
        return;
      }

      if (event.data && event.data.appleToken) {
        try {
          console.log("✅ Apple login success data:", event.data);
          if (setConsumerToken) {
            setConsumerToken(event.data.appleToken);
          } else {
            localStorage.setItem("consumerToken", event.data.appleToken);
          }
          onAuthSuccess({
            isAuthenticated: true,
            consumerId: event.data.consumerId,
            email: event.data.email,
            clientId: event.data.clientId || configClientId,
            requiresTwoFactor: false,
            credits: event.data.credits,
          });
        } catch (err) {
          console.error("❌ Error processing Apple login success:", err);
          const errorMsg = `Apple login processing failed: ${err instanceof Error ? err.message : "Unknown error"}`;
          setError(errorMsg);
          showSnackbar?.(errorMsg, "error");
        } finally {
          setSocialLoading("");
        }
        cleanup();
        if (popup) popup.close();
      }

      if (event.data && (event.data.appleError || event.data.appleErrorDescription)) {
        console.error("❌ Apple login error:", event.data);
        const errorMsg = event.data.appleErrorDescription || event.data.appleError || "Apple login failed";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        setSocialLoading("");
        cleanup();
        if (popup) popup.close();
      }
    };

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeoutId);
      clearInterval(checkClosedId);
      cleanupRef.current = null;
    };

    cleanupRef.current = cleanup;
    window.addEventListener("message", handleMessage);

    checkClosedId = setInterval(() => {
      if (popup?.closed) {
        console.log("🍎 Apple popup closed manually");
        setSocialLoading("");
        cleanup();
      }
    }, 1000);

    timeoutId = setTimeout(() => {
      console.log("⏰ Apple login timeout");
      const errorMsg = "Apple login timeout. Please try again.";
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
      setSocialLoading("");
      cleanup();
      if (popup && !popup.closed) popup.close();
    }, 120000);
  }, [getClientId, configClientId, onAuthSuccess, setConsumerToken, showSnackbar, onRequiresDob, onStateChange]);

  const handleFacebookLogin = useCallback((options = {}) => {
    const { mode = "login", dateOfBirth = null } = options;
    setSocialLoading("facebook");
    setError("");

    const clientId = getClientId();

    // Build popup URL with mode and dateOfBirth
    let popupUrl = `${window.location.origin}/facebook-login-popup?clientId=${encodeURIComponent(clientId || "")}&mode=${encodeURIComponent(mode)}`;
    if (dateOfBirth) {
      popupUrl += `&dateOfBirth=${encodeURIComponent(dateOfBirth)}`;
    }

    const popup = window.open(
      popupUrl,
      "FacebookLoginPopup",
      "width=500,height=600"
    );

    let checkClosedId;

    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      // Handle requiresDob response - need to collect DOB before completing registration
      if (event.data && event.data.requiresDob && event.data.provider === "facebook") {
        console.log("📋 Facebook OAuth requires DOB collection:", event.data);
        const pendingData = {
          provider: "facebook",
          email: event.data.email,
          name: event.data.name,
          oauthSessionToken: event.data.oauthSessionToken, // Use session token instead of code
          clientId: event.data.clientId || configClientId,
        };
        
        // Store pending OAuth data and navigate to collectDob
        if (onRequiresDob) {
          onRequiresDob(pendingData);
        }
        if (onStateChange) {
          onStateChange("collectDob");
        }
        
        setSocialLoading("");
        cleanup();
        if (popup) popup.close();
        return;
      }

      if (event.data && event.data.facebookToken) {
        try {
          if (setConsumerToken) {
            setConsumerToken(event.data.facebookToken);
          } else {
            localStorage.setItem("consumerToken", event.data.facebookToken);
          }
          onAuthSuccess({
            isAuthenticated: true,
            consumerId: event.data.consumerId,
            email: event.data.email,
            clientId: event.data.clientId || configClientId,
            requiresTwoFactor: false,
            credits: event.data.credits,
          });
        } catch (err) {
          const errorMsg = "Facebook login failed";
          setError(errorMsg);
          showSnackbar?.(errorMsg, "error");
        } finally {
          setSocialLoading("");
        }
        cleanup();
        popup?.close();
      }

      if (event.data && (event.data.facebookError || event.data.facebookErrorDescription)) {
        const errorMsg = event.data.facebookErrorDescription || "Facebook login failed";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        setSocialLoading("");
        cleanup();
        popup?.close();
      }
    };

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      clearInterval(checkClosedId);
      cleanupRef.current = null;
    };

    cleanupRef.current = cleanup;
    window.addEventListener("message", handleMessage);

    checkClosedId = setInterval(() => {
      if (popup?.closed) {
        setSocialLoading("");
        cleanup();
      }
    }, 1000);
  }, [getClientId, configClientId, onAuthSuccess, setConsumerToken, showSnackbar, onRequiresDob, onStateChange]);

  return {
    socialLoading,
    error,
    setError,
    handleGoogleLogin,
    handleAppleLogin,
    handleFacebookLogin,
  };
};

export default useSocialAuth;